import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-power text-white",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "text-foreground",
        // Status variants — tinted background + colored text. Backgrounds are
        // OPAQUE pre-computed tints (success.light etc), not alpha overlays,
        // so chips on a selected (bg-power-light) row don't blend muddy.
        success: "border-transparent bg-success-light text-success",
        warning: "border-transparent bg-warning-light text-warning",
        danger: "border-transparent bg-danger-light text-danger",
        info: "border-transparent bg-info-light text-info",
        neutral: "border-transparent bg-neutral-light text-foreground",
        // Filled status variants — use sparingly when the chip itself needs
        // to attract attention (e.g. a row-level "ACTION REQUIRED" pill).
        "success-solid": "border-transparent bg-success text-success-foreground",
        "warning-solid": "border-transparent bg-warning text-warning-foreground",
        "danger-solid": "border-transparent bg-danger text-danger-foreground",
        "info-solid": "border-transparent bg-info text-info-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
